import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { cloneElement, isValidElement, type CSSProperties, type ReactNode } from "react";
import { slugificar } from "../lib/docs-content";

const marca: CSSProperties = {
  background: "color-mix(in srgb, var(--accent) 32%, transparent)",
  color: "var(--text)",
  borderRadius: 3,
  padding: "0 1px",
  fontWeight: 600,
};

/**
 * Envolve cada ocorrencia de `termo` (case-insensitive) num `<mark data-doc-hit>`.
 *
 * Percorre a arvore de nos que o react-markdown ja produziu: em string, parte no
 * termo e marca os pedacos; em elemento, desce nos filhos preservando o elemento
 * (um `<strong>` dentro de um `<p>` continua negrito, e o termo dentro dele
 * tambem fica marcado). O `data-doc-hit` e' a ancora que o DocsShell usa pra
 * rolar ate a ocorrencia e destacar a ativa.
 */
function realcar(node: ReactNode, termo: string, chave = "h"): ReactNode {
  if (!termo) return node;
  if (typeof node === "string") {
    const alvo = termo.toLowerCase();
    const baixo = node.toLowerCase();
    if (!baixo.includes(alvo)) return node;
    const partes: ReactNode[] = [];
    let i = 0;
    let n = 0;
    for (let idx = baixo.indexOf(alvo, i); idx !== -1; idx = baixo.indexOf(alvo, i)) {
      if (idx > i) partes.push(node.slice(i, idx));
      partes.push(
        <mark key={`${chave}-${n++}`} data-doc-hit="" style={marca}>{node.slice(idx, idx + alvo.length)}</mark>,
      );
      i = idx + alvo.length;
    }
    if (i < node.length) partes.push(node.slice(i));
    return partes;
  }
  if (Array.isArray(node)) return node.map((f, i) => realcar(f, termo, `${chave}-${i}`));
  if (isValidElement(node)) {
    const el = node as React.ReactElement<{ children?: ReactNode }>;
    // Nao realca dentro de <code>/<pre>: marcar texto em bloco de codigo bagunca
    // o alinhamento e nao ajuda a ler.
    if (el.type === "code" || el.type === "pre") return node;
    if (el.props?.children != null) {
      return cloneElement(el, { ...el.props, children: realcar(el.props.children, termo, chave) });
    }
  }
  return node;
}

/**
 * Markdown da documentacao de integracao, com GFM.
 *
 * GFM nao e opcional aqui: as tabelas de campo (`Campo | Formato | Obrigatorio |
 * Descricao | Exemplo`), que sao o formato de manual de integracao bancaria, so
 * existem com essa extensao.
 *
 * Os estilos vem dos tokens CSS do design system, entao a documentacao acompanha
 * tema claro e escuro sem trabalho extra. Nada de hex hardcoded.
 */

const pre: CSSProperties = {
  padding: 14,
  background: "var(--bg-elev-2)",
  borderRadius: 8,
  overflowX: "auto",
  fontFamily: "var(--font-mono)",
  fontSize: 12.5,
  lineHeight: 1.6,
  color: "var(--text)",
  margin: "0 0 16px",
  border: "1px solid var(--border)",
};

const codigoInline: CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: "0.88em",
  padding: "1px 5px",
  borderRadius: 4,
  background: "var(--bg-elev-2)",
  border: "1px solid var(--border)",
};

/** Heading com id derivado do texto, pra ancora e indice lateral casarem. */
function heading(nivel: 2 | 3 | 4, style: CSSProperties, realce: string) {
  return function H({ children }: { children?: React.ReactNode }) {
    // O id sai do texto CRU (sem realce), pra ancora nao quebrar quando o termo
    // muda; so o conteudo visivel ganha as marcas.
    const texto = extrairTexto(children);
    const Tag = (`h${nivel}` as unknown) as "h2";
    return (
      <Tag id={slugificar(texto)} style={{ ...style, scrollMarginTop: 90 }}>
        {realcar(children, realce)}
      </Tag>
    );
  };
}

function extrairTexto(n: React.ReactNode): string {
  if (typeof n === "string") return n;
  if (Array.isArray(n)) return n.map(extrairTexto).join("");
  if (n && typeof n === "object" && "props" in n) {
    return extrairTexto((n as { props: { children?: React.ReactNode } }).props.children);
  }
  return "";
}

/**
 * Markdown da documentacao. `realce`, quando dado, marca (`<mark data-doc-hit>`)
 * cada ocorrencia do termo no texto de leitura — e o que o DocsShell usa pra
 * "deixar a busca marcada" na pagina aberta a partir de um resultado.
 *
 * `onLinkInterno` recebe o href de um link que NAO e' externo (comeca com `/`,
 * herdado dos caminhos do antigo site Docusaurus) pra o DocsShell resolver em
 * navegacao dentro da SPA. Sem ele, esses links dariam um full-load numa rota
 * que nao existe — que e' o bug que isto conserta.
 */
export function Markdown({
  children,
  realce = "",
  onLinkInterno,
}: {
  children: string;
  realce?: string;
  onLinkInterno?: (href: string) => void;
}) {
  const rc = (n: React.ReactNode) => realcar(n, realce);
  return (
    <div style={{ fontSize: 14.5, lineHeight: 1.75, color: "var(--text)", maxWidth: 820 }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => (
            <h1 style={{ fontSize: "1.8rem", letterSpacing: "-0.02em", margin: "0 0 12px" }}>{rc(children)}</h1>
          ),
          h2: heading(2, { fontSize: "1.25rem", margin: "32px 0 10px", paddingTop: 8, borderTop: "1px solid var(--border)" }, realce),
          h3: heading(3, { fontSize: "1.05rem", margin: "24px 0 8px" }, realce),
          h4: heading(4, { fontSize: "0.95rem", margin: "18px 0 6px", color: "var(--text-muted)" }, realce),
          p: ({ children }) => <p style={{ margin: "0 0 14px" }}>{rc(children)}</p>,
          a: ({ children, href }) => {
            const externo = !!href && /^(https?:|mailto:|tel:)/i.test(href);
            if (externo) {
              return (
                <a href={href} target="_blank" rel="noreferrer" style={{ color: "var(--accent)" }}>
                  {rc(children)}
                </a>
              );
            }
            // Âncora #id rola no próprio container (headings têm id) — deixa o
            // navegador agir. Link interno de doc (/referencia/…, /guias/…, /erros)
            // é resolvido pela SPA, sem full-load numa rota inexistente.
            const ancora = !!href && href.startsWith("#");
            return (
              <a
                href={href}
                style={{ color: "var(--accent)" }}
                onClick={(e) => {
                  if (ancora || !href) return;
                  e.preventDefault();
                  onLinkInterno?.(href);
                }}
              >
                {rc(children)}
              </a>
            );
          },
          ul: ({ children }) => <ul style={{ margin: "0 0 14px", paddingLeft: 22 }}>{children}</ul>,
          ol: ({ children }) => <ol style={{ margin: "0 0 14px", paddingLeft: 22 }}>{children}</ol>,
          li: ({ children }) => <li style={{ margin: "0 0 6px" }}>{rc(children)}</li>,
          strong: ({ children }) => <b style={{ color: "var(--text)" }}>{rc(children)}</b>,
          blockquote: ({ children }) => (
            <blockquote style={{
              margin: "0 0 14px", padding: "8px 14px", borderLeft: "3px solid var(--accent)",
              background: "var(--bg-elev-2)", borderRadius: "0 8px 8px 0", color: "var(--text-muted)",
            }}>
              {children}
            </blockquote>
          ),
          hr: () => <hr style={{ border: "none", borderTop: "1px solid var(--border)", margin: "28px 0" }} />,
          // Tabela larga rola dentro do proprio container: a pagina nunca ganha
          // barra horizontal por causa de uma tabela de campos.
          table: ({ children }) => (
            <div style={{ overflowX: "auto", margin: "0 0 18px", border: "1px solid var(--border)", borderRadius: 8 }}>
              <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }}>{children}</table>
            </div>
          ),
          thead: ({ children }) => <thead style={{ background: "var(--bg-elev-2)" }}>{children}</thead>,
          th: ({ children }) => (
            <th style={{
              textAlign: "left", padding: "9px 12px", fontWeight: 700, fontSize: 11.5,
              textTransform: "uppercase", letterSpacing: ".04em", color: "var(--text-dim)",
              borderBottom: "1px solid var(--border)", whiteSpace: "nowrap",
            }}>
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td style={{ padding: "9px 12px", borderTop: "1px solid var(--border)", verticalAlign: "top" }}>{rc(children)}</td>
          ),
          pre: ({ children }) => <pre style={pre}>{children}</pre>,
          code: ({ children, className }) =>
            // Bloco cercado vem com className `language-*`; inline vem sem.
            className ? <code className={className}>{children}</code> : <code style={codigoInline}>{children}</code>,
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}
